import { useState } from 'react';
import { Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { createStore } from '@/lib/store';
import { post } from '@/lib/api';
import { plural } from '@/lib/format';
import { attempt, notify } from '@/lib/notify';
import { cn } from '@/lib/utils';

const store = createStore<{ feedId: string; onDone: () => void } | null>(null);
export const openImportDialog = (feedId: string, onDone: () => void) => store.set({ feedId, onDone });

export function ImportDialog() {
  const s = store.use();
  const [file, setFile] = useState<File | null>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={!!s} onOpenChange={(o) => { if (!o) { store.set(null); setFile(null); } }}>
      <DialogContent>
        <form
          className="grid grid-cols-[minmax(0,1fr)] gap-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!s) return;
            if (!file) return notify('Choose a file first', { kind: 'error' });
            setBusy(true);
            const r = await attempt(async () => post<{ imported: number }>(`/feeds/${s.feedId}/import`, { ics: await file.text() }));
            setBusy(false);
            if (!r) return;
            notify(`Imported ${plural(r.imported, 'event')}`);
            store.set(null);
            setFile(null);
            s.onDone();
          }}
        >
          <DialogHeader>
            <DialogTitle>Import events</DialogTitle>
            <DialogDescription>Copies the events in the file into this calendar, once. Importing the same file again updates those events instead of duplicating them. To stay in sync with a live calendar, use “Connect a calendar” and add it as a layer instead.</DialogDescription>
          </DialogHeader>
          <label
            className={cn('hover:border-primary hover:bg-brand-soft flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-dashed p-8 text-center transition-colors', over && 'border-primary bg-brand-soft')}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}
          >
            <input type="file" accept=".ics,text/calendar" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <Upload className="text-muted-foreground size-6" />
            <span className="font-medium">{file ? file.name : 'Choose an .ics file'}</span>
            <span className="text-muted-foreground text-xs">or drag it here</span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => store.set(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Importing…' : 'Import'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
